import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    CeButtonComponent,
    CeIconComponent,
    CeInputImports,
    CeSnackbarService,
    CeSnackbarState,
    CeSpinningLoaderComponent,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { dateValidator } from '../../utils/date-validator';
import { getTodayFormatted } from '../../utils/get-today-formatted';
import { convertDateToIso } from '../../utils/date-utils';
import { ConfigService } from '@kempower-nx-workspace/receipt-lookup-environment';
import { ReceiptSearchService, SearchResult } from '../../data-access/receipt-search.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReceiptLookupNavigationService } from '@kempower-nx-workspace/receipt-lookup-app-core';
import { CaptchaService } from '../../data-access/captcha.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'lib-landing-page',
    imports: [
        CommonModule,
        CeInputImports,
        CeButtonComponent,
        CeIconComponent,
        ReactiveFormsModule,
        CeSpinningLoaderComponent,
    ],
    templateUrl: './receipt-lookup-form.component.html',
    styleUrl: './receipt-lookup-form.component.scss'
})
export class ReceiptLookupFormComponent {
    readonly #fb: FormBuilder = inject(FormBuilder);
    readonly #config: ConfigService = inject(ConfigService);
    readonly #service: ReceiptSearchService = inject(ReceiptSearchService);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #navigation: ReceiptLookupNavigationService = inject(ReceiptLookupNavigationService);
    readonly #snackbar = inject(CeSnackbarService);
    readonly #captcha = inject(CaptchaService);

    #showingCaptcha = false;
    #pendingFormSubmission: { customerId: string; isoDate: string; lastFourDigits: string } | null = null;

    // In case customerId is undefined, it would already have been handled by pageNotFoundGuard
    readonly customerId: string = this.#config.extractedCustomerId ?? '';

    // Use the service's loading state directly
    get isLoading(): boolean {
        return this.#service.currentResults().loading || this.#showingCaptcha;
    }

    readonly chargingDateControl: FormControl<string | null> = this.#fb.control(getTodayFormatted(), [
        Validators.required,
        dateValidator,
    ]);
    readonly paymentCardLastFourDigitsControl: FormControl<string | null> = this.#fb.control('', [
        Validators.required,
        Validators.pattern(/^\d{4}$/),
    ]);

    receiptForm: FormGroup<{
        chargingDate: FormControl<string | null>;
        paymentCardLastFourDigits: FormControl<string | null>;
    }> = this.#fb.group({
        chargingDate: this.chargingDateControl,
        paymentCardLastFourDigits: this.paymentCardLastFourDigitsControl,
    });

    async onSubmit(): Promise<void> {
        if (this.receiptForm.invalid) {
            this.receiptForm.markAllAsTouched();
            return;
        }

        const formValue = this.receiptForm.value;
        const chargingDate = formValue.chargingDate;
        const lastFourDigits = formValue.paymentCardLastFourDigits;

        if (!chargingDate || !lastFourDigits) {
            console.error('Form values are missing');
            return;
        }

        // Disable form controls during loading
        this.receiptForm.disable();

        const isoDate: string = convertDateToIso(chargingDate);

        // Check if CAPTCHA token exists before submitting
        try {
            await this.#captcha.showPromptIfNoCookie();
            this.#submitSearchRequest(this.customerId, isoDate, lastFourDigits);
        } catch (error) {
            this.#handleCaptchaVerificationError();
        }
    }

    #submitSearchRequest(customerId: string, isoDate: string, lastFourDigits: string): void {
        this.#service
            .searchReceipts$(customerId, isoDate, lastFourDigits)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: (result: SearchResult) => {
                    // Update service state for each emitted value
                    this.#service.updateResults(result);
                    if (!result.loading) {
                        // Re-enable form controls when loading is complete
                        this.receiptForm.enable();

                        if (result.error) {
                            // Handle different error categories
                            if (this.#isRateLimitError(result.errorStatus)) {
                                this.#handleRateLimitError();
                            } else if (this.#isCaptchaError(result.errorObject)) {
                                this.#handleCaptchaError();
                            } else {
                                this.#handleGenericError();
                            }
                        } else if (result.data) {
                            this.#resetFormAfterSuccess();
                            this.#navigation.navigateToResult();
                        }
                    }
                },
                error: (error) => {
                    console.error('Unexpected error:', error);
                    // Re-enable form controls on error
                    this.receiptForm.enable();
                    // Update service with error state
                    this.#service.updateResults({
                        loading: false,
                        data: undefined,
                        error: 'An unexpected error occurred',
                    });
                },
            });
    }

    #resetFormAfterSuccess(): void {
        this.receiptForm.reset();
        this.receiptForm.patchValue({
            chargingDate: getTodayFormatted(),
            paymentCardLastFourDigits: '',
        });
        this.receiptForm.markAsUntouched();
        this.#pendingFormSubmission = null;
    }

    #isRateLimitError(errorStatus: number | undefined | null): boolean {
        return errorStatus === 403;
    }

    #handleRateLimitError(): void {
        this.#snackbar.showSnackbar({
            message: 'You’ve hit the limit – take a short break and try again.',
            state: CeSnackbarState.ERROR,
            icon: 'error',
            showCloseButton: true,
        });
    }

    #isCaptchaError(error: HttpErrorResponse | undefined): boolean {
        if (!error) return false;
        const wafAction = error?.headers?.get?.('x-amzn-waf-action');
        return error?.status === 405 && wafAction === 'captcha';
    }

    #handleCaptchaError(): void {
        if (this.#showingCaptcha) {
            return;
        }

        const formValue = this.receiptForm.value;
        const chargingDate = formValue.chargingDate;
        const lastFourDigits = formValue.paymentCardLastFourDigits;

        if (chargingDate && lastFourDigits) {
            const isoDate: string = convertDateToIso(chargingDate);
            this.#pendingFormSubmission = {
                customerId: this.customerId,
                isoDate,
                lastFourDigits,
            };
        }

        this.#showingCaptcha = true;

        this.#captcha
            .prompt()
            .then(() => {
                this.#showingCaptcha = false;
                if (this.#pendingFormSubmission) {
                    const { customerId, isoDate, lastFourDigits } = this.#pendingFormSubmission;
                    this.#pendingFormSubmission = null;

                    // Retry the request
                    this.#submitSearchRequest(customerId, isoDate, lastFourDigits);
                }
            })
            .catch((e) => {
                this.#showingCaptcha = false;
                this.#pendingFormSubmission = null;
                this.#handleCaptchaVerificationError();
            });
    }

    #handleCaptchaVerificationError(): void {
        this.#snackbar.showSnackbar({
            message: 'CAPTCHA verification failed – please try again.',
            state: CeSnackbarState.ERROR,
            icon: 'error',
            showCloseButton: true,
        });
    }

    #handleGenericError(): void {
        this.#snackbar.showSnackbar({
            message: 'Something went wrong on our end – try again later.',
            state: CeSnackbarState.ERROR,
            icon: 'error',
            showCloseButton: true,
        });
    }
}
